import { normalizeLocale } from '@hqo/date-time';
import { flatten } from 'app/shared/utils/flatten';
import { useMemo } from 'react';

import messagesInEnglish from 'app/lang/en.json';
import messagesInEnglishAU from 'app/lang/en-AU.json';
import messagesInEnglishCA from 'app/lang/en-CA.json';
import messagesInEnglishGB from 'app/lang/en-GB.json';
import messagesInEnglishIE from 'app/lang/en-IE.json';
import messagesInEnglishUS from 'app/lang/en-US.json';
import messagesInGermanDE from 'app/lang/de-DE.json';
import messagesInSpanishES from 'app/lang/es-ES.json';
import messagesInFrenchCA from 'app/lang/fr-CA.json';
import messagesInFrenchFR from 'app/lang/fr-FR.json';
import messagesInHungarianHU from 'app/lang/hu-HU.json';
import messagesInItalianIT from 'app/lang/it-IT.json';
import messagesInNorwegianNB from 'app/lang/nb-NO.json';
import messagesInDutchNL from 'app/lang/nl-NL.json';
import messagesInPolishPL from 'app/lang/pl.json';
import messagesInPortuguesePT from 'app/lang/pt-PT.json';
import messagesInSlovakSK from 'app/lang/sk-SK.json';

/**
 * Region-less on purpose. A region-less default lets the DEVICE decide date order and 12h/24h via
 * `@hqo/date-time`'s presentation axis; hardcoding `en-US` here would pin a US clock on a London
 * user whose host passed no locale.
 */
export const DEFAULT_LOCALE = 'en';

/**
 * Every catalog this miniapp ships, keyed by lowercased BCP-47 tag. Static named imports (rather than
 * a dynamic `import()`) keep resolution synchronous and the bundle limited to exactly these locales.
 *
 * Add a locale by adding a row here AND a `lang/<tag>.json`. `translations.spec.ts` fails if the two
 * ever disagree, so neither half can be forgotten.
 */
const rawMessagesMap = new Map<string, Record<string, unknown>>([
  ['en', messagesInEnglish.baseMiniApp],
  ['en-au', messagesInEnglishAU.baseMiniApp],
  ['en-ca', messagesInEnglishCA.baseMiniApp],
  ['en-gb', messagesInEnglishGB.baseMiniApp],
  ['en-ie', messagesInEnglishIE.baseMiniApp],
  ['en-us', messagesInEnglishUS.baseMiniApp],
  ['de-de', messagesInGermanDE.baseMiniApp],
  ['es-es', messagesInSpanishES.baseMiniApp],
  // Bare `fr` is declared EXPLICITLY as France. French is the one language here with two regional
  // catalogs, so leaving bare `fr` to derivation would make its meaning depend on this Map's
  // insertion order — reordering these rows alphabetically would silently hand `fr` to Quebec.
  ['fr', messagesInFrenchFR.baseMiniApp],
  ['fr-ca', messagesInFrenchCA.baseMiniApp],
  ['fr-fr', messagesInFrenchFR.baseMiniApp],
  ['hu-hu', messagesInHungarianHU.baseMiniApp],
  ['it-it', messagesInItalianIT.baseMiniApp],
  ['nb-no', messagesInNorwegianNB.baseMiniApp],
  ['nl-nl', messagesInDutchNL.baseMiniApp],
  ['pl', messagesInPolishPL.baseMiniApp],
  ['pt-pt', messagesInPortuguesePT.baseMiniApp],
  ['sk-sk', messagesInSlovakSK.baseMiniApp],
]);

/**
 * Fills in a bare-language entry for any language that has exactly one regional catalog, so a host
 * passing `de` (no region) gets German rather than falling all the way back to English.
 *
 * A tag declared explicitly above — `en`, `fr`, `pl` — is never overwritten. That matters: any
 * language with MORE than one regional catalog must be declared explicitly, because derivation would
 * otherwise resolve it by this Map's insertion order, making the result depend on row ordering rather
 * than on a decision anyone made.
 */
const withDerivedBaseLocales = (source: Map<string, Record<string, unknown>>) => {
  const derived = new Map(source);

  source.forEach((messages, tag) => {
    const [language] = tag.split('-');

    if (language !== tag && !derived.has(language)) {
      derived.set(language, messages);
    }
  });

  return derived;
};

const englishBase = flatten(messagesInEnglish.baseMiniApp) as Record<string, string>;

/**
 * Each catalog is merged OVER English rather than used on its own.
 *
 * This is what lets a regional file be sparse — the five English regionals ship empty, because their
 * job is to make the TAG resolve (so dates/numbers go regional) and there is no spelling divergence
 * to carry. It also means a partially-translated catalog renders its translated keys and falls back
 * to English for the rest, instead of rendering a raw message id.
 */
const messagesMap = new Map<string, Record<string, string>>();
withDerivedBaseLocales(rawMessagesMap).forEach((messages, tag) => {
  messagesMap.set(tag, { ...englishBase, ...(flatten(messages) as Record<string, string>) });
});

/**
 * Picks the catalog to render with, and the locale tag to report to `react-intl`.
 *
 * Returns `[messagesKey, locale]`, and the two are deliberately different things:
 *
 * - **`locale`** is CANONICAL BCP-47, via `@hqo/date-time`'s `normalizeLocale` — so `en_us` and
 *   `EN-US` both report `en-US`. This value becomes `useIntl().locale` and flows straight into the
 *   formatters, so it has to be a tag `Intl` is happy to receive rather than whatever casing the host
 *   happened to send. Reusing the package's resolver also keeps one definition of "canonical" on the
 *   platform instead of a second, subtly different one in here.
 * - **`messagesKey`** is that canonical tag lowercased, because that is how the catalog Map is keyed.
 *   Deriving it from the canonical form rather than from the raw input is deliberate: the
 *   underscore-to-hyphen conversion then happens in exactly one place (the package) instead of being
 *   reimplemented here and drifting.
 *
 * The two also differ in a second, load-bearing way: a regional tag with no catalog of its own falls
 * back to its LANGUAGE catalog but still reports the full regional tag. `de-AT` renders the `de` words
 * while reporting `de-AT`, so Austrian date conventions survive. Collapsing the two would throw the
 * region away and silently hand `de-AT` German-of-Germany formatting.
 */
export const getAvailableLocale = (userLocale: string): [string, string] => {
  // Guard before normalizing: the package's `normalizeLocale` resolves empty input to its OWN default
  // (`en-US`), which would quietly promote "the host told us nothing" into a US regional choice.
  if (!userLocale || !userLocale.trim()) {
    return [DEFAULT_LOCALE, DEFAULT_LOCALE];
  }

  const canonical = normalizeLocale(userLocale);
  const lookupKey = canonical.toLowerCase();

  if (messagesMap.has(lookupKey)) {
    return [lookupKey, canonical];
  }

  const [language] = lookupKey.split('-');

  if (messagesMap.has(language)) {
    return [language, canonical];
  }

  return [DEFAULT_LOCALE, DEFAULT_LOCALE];
};

interface LocaleData {
  messages: Record<string, string>;
  locale: string;
}

export const useLocaleData = (userLocale: string): LocaleData =>
  useMemo(() => {
    const [messagesKey, locale] = getAvailableLocale(userLocale);

    return {
      locale,
      // `getAvailableLocale` only ever returns a key it found in the map (or DEFAULT_LOCALE, which is
      // always present), so this cannot miss — but fall back rather than assert, so a future edit to
      // the map can't turn a missing catalog into a blank render.
      messages: messagesMap.get(messagesKey) ?? messagesMap.get(DEFAULT_LOCALE)!,
    };
  }, [userLocale]);
